import { useMemo, useState } from 'react';
import {
  AreaChartIcon,
  Banner,
  Card,
  CardContent,
  EmptyState,
  GraphIcon,
  ImportIcon,
  SuccessIcon,
  useDocumentTitle,
  useTheme,
} from '@forge/common';
import type { DashboardStatus } from '@/api/dashboard';
import { useDashboard } from '@/hooks/useDashboard';
import ActivityFeed from './components/ActivityFeed';
import { getAccentColors, getStatusColors } from './components/chartColors';
import DashboardHeader from './components/DashboardHeader';
import type { RangeMonths } from './components/DashboardHeader';
import InitiativeProgress from './components/InitiativeProgress';
import KpiCard from './components/KpiCard';
import MilestoneList from './components/MilestoneList';
import ProgressTrendChart from './components/ProgressTrendChart';
import ReleaseCadenceChart from './components/ReleaseCadenceChart';
import ReleaseList from './components/ReleaseList';
import RoadmapView from './components/RoadmapView';
import StatusDonutChart from './components/StatusDonutChart';

const statusOrder: Record<DashboardStatus, number> = {
  in_progress: 0,
  blocked: 1,
  todo: 2,
  done: 3,
};

const formatSnapshotDate = (value: string): string => {
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));
};

const DashboardSkeleton = () => {
  return (
    <div className="space-y-6">
      <div className="h-9 w-72 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-800" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-36 animate-pulse rounded-2xl bg-gray-200 dark:bg-gray-800"
          />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="h-80 animate-pulse rounded-2xl bg-gray-200 dark:bg-gray-800 xl:col-span-2" />
        <div className="h-80 animate-pulse rounded-2xl bg-gray-200 dark:bg-gray-800" />
      </div>
    </div>
  );
};

const StatusDashboardPage = () => {
  useDocumentTitle('Dashboard — OneRing Portal');

  const { isDark } = useTheme();
  const [rangeMonths, setRangeMonths] = useState<RangeMonths>(6);

  const { summary, timeline, isLoading, isUsingMockData, reload } =
    useDashboard(rangeMonths);

  const featuredMilestones = useMemo(() => {
    return [...(summary?.topMilestones ?? [])].sort((left, right) => {
      return (
        statusOrder[left.status] - statusOrder[right.status] ||
        right.completionPct - left.completionPct ||
        left.sortOrder - right.sortOrder
      );
    });
  }, [summary]);

  const releaseSummaries = useMemo(() => {
    return [...(summary?.repoReleases ?? [])].sort((left, right) => {
      const leftDate = left.releases[0]?.publishedAt ?? '0';
      const rightDate = right.releases[0]?.publishedAt ?? '0';

      return new Date(rightDate).getTime() - new Date(leftDate).getTime();
    });
  }, [summary]);

  const roadmapYear = useMemo(() => {
    return summary?.currentRoadmap[0]?.year ?? 2026;
  }, [summary]);

  const trendPoints = useMemo(() => {
    const points = summary?.trends.points ?? [];
    return points.slice(Math.max(0, points.length - rangeMonths));
  }, [summary, rangeMonths]);

  const filteredTimeline = useMemo(() => {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - rangeMonths);
    const cutoffTime = cutoff.getTime();

    return timeline.filter((event) => {
      return new Date(event.date).getTime() >= cutoffTime;
    });
  }, [timeline, rangeMonths]);

  const latestSnapshotDate = useMemo(() => {
    const dates = [
      ...(summary?.topMilestones.map((milestone) => milestone.updatedAt) ?? []),
      ...timeline.map((event) => event.date),
    ];

    if (dates.length === 0) {
      return undefined;
    }

    return [...dates].sort((left, right) => {
      return new Date(right).getTime() - new Date(left).getTime();
    })[0];
  }, [summary, timeline]);

  if (isLoading && !summary) {
    return (
      <div className="p-4 md:p-6">
        <DashboardSkeleton />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="p-6">
        <Card>
          <CardContent>
            <EmptyState
              icon={<GraphIcon />}
              description="Dashboard data unavailable"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  const accents = getAccentColors(isDark);
  const status = getStatusColors(isDark);
  const lastPoint = trendPoints[trendPoints.length - 1];
  const recentCompleted = lastPoint?.milestonesCompleted ?? 0;
  const recentReleases = lastPoint?.releases ?? 0;
  const updatedLabel = latestSnapshotDate
    ? formatSnapshotDate(latestSnapshotDate)
    : undefined;

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <DashboardHeader
        isUsingMockData={isUsingMockData}
        updatedLabel={updatedLabel}
        rangeMonths={rangeMonths}
        onRangeChange={setRangeMonths}
        onRefresh={reload}
      />

      {isUsingMockData ? (
        <Banner
          variant="inline"
          type="info"
          title=""
          hideIcon
          canClose={false}
          description="Dashboard is displaying mock data for demonstration purposes. Live data will be shown when the API is connected."
        />
      ) : undefined}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={
            <AreaChartIcon
              aria-hidden
              className="h-5 w-5 text-gray-700 dark:text-gray-100"
            />
          }
          value={summary.metrics.activeInitiatives}
          label="Active Initiatives"
          accentColor={accents.violet}
          detail={
            <p className="text-xs leading-5 text-color-tertiary">
              Cross-functional platform programs currently in motion
            </p>
          }
        />
        <KpiCard
          icon={
            <GraphIcon
              aria-hidden
              className="h-5 w-5 text-gray-700 dark:text-gray-100"
            />
          }
          value={`${summary.metrics.overallProgressPct}%`}
          label="Overall Progress"
          accentColor={accents.blue}
          detail={
            <div className="space-y-2">
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${summary.metrics.overallProgressPct}%`,
                    backgroundColor: accents.blue,
                  }}
                />
              </div>
              <div className="text-[11px] font-medium text-color-tertiary">
                <span>{summary.metrics.overallProgressPct}% complete</span>
              </div>
              <p className="text-xs leading-5 text-color-tertiary">
                Portfolio delivery against the current quarter baseline
              </p>
            </div>
          }
        />
        <KpiCard
          icon={
            <SuccessIcon
              aria-hidden
              className="h-5 w-5 text-gray-700 dark:text-gray-100"
            />
          }
          value={`${summary.metrics.completedMilestones} of ${summary.metrics.totalMilestones}`}
          label="Milestones Completed"
          accentColor={status.done}
          trend={{
            label: `+${recentCompleted} this month`,
            direction: recentCompleted > 0 ? 'up' : 'neutral',
          }}
          sparkline={trendPoints.map((point) => point.cumulativeCompleted)}
        />
        <KpiCard
          icon={
            <ImportIcon
              aria-hidden
              className="h-5 w-5 text-gray-700 dark:text-gray-100"
            />
          }
          value={summary.metrics.releasesThisQuarter}
          label="Releases This Quarter"
          accentColor={accents.amber}
          trend={{
            label: `+${recentReleases} this month`,
            direction: recentReleases > 0 ? 'up' : 'neutral',
          }}
          sparkline={trendPoints.map((point) => point.releases)}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <ProgressTrendChart points={trendPoints} />
        </div>
        <StatusDonutChart distribution={summary.trends.statusDistribution} />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <InitiativeProgress categories={summary.categorySummaries} />
        <ReleaseCadenceChart points={trendPoints} />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.5fr,1fr]">
        <MilestoneList milestones={featuredMilestones} />
        <ActivityFeed events={filteredTimeline} />
      </div>

      <RoadmapView items={summary.currentRoadmap} year={roadmapYear} />

      <ReleaseList summaries={releaseSummaries} />
    </div>
  );
};

export default StatusDashboardPage;
